// Umami analytics loader, gated to the production domains.
//
// The script used to be hardcoded in index.html, so every deployment (incl. the
// test/staging server, which serves the same build) reported pageviews/events
// into the same production Umami website — falsifying the stats. We now inject it
// at runtime only when the page is served from a canonical production host, so the
// test server, droplet IPs, preview hosts and localhost send zero analytics.

import { isAnalyticsAllowed } from './cookie-consent';

const UMAMI_SRC = 'https://analytics.draughtsgame.com/script.js';
const UMAMI_WEBSITE_ID = 'ed088b4f-2aad-4494-98ab-e5dfddd372dd';
const PRODUCTION_HOSTS = ['draughtsgame.com', 'www.draughtsgame.com'];

/** True only on the canonical production domains that should emit analytics. */
export function isProductionHost(hostname: string): boolean {
  return PRODUCTION_HOSTS.includes(hostname);
}

/**
 * Inject the Umami tracking script — but only on the production domains. A no-op
 * everywhere else (test server, IPs, previews, localhost), so `window.umami`
 * stays undefined and `analytics.ts` track() silently no-ops.
 *
 * `quandoPronto` viene chiamata quando `window.umami` esiste davvero — cioe' al
 * caricamento dello script, o subito se era gia' li'. Serve a chi vuole
 * misurare un evento NELL'ISTANTE in cui il consenso arriva: senza, quella
 * chiamata partirebbe mentre lo script si sta ancora scaricando e
 * `analytics.ts` la butterebbe via in silenzio (`if (!window.umami) return`).
 * Se il consenso manca, o il dominio non e' quello di produzione, non viene
 * chiamata affatto: non c'e' niente da misurare.
 */
export function loadUmami(
  hostname: string = window.location.hostname,
  quandoPronto?: () => void,
): void {
  if (!isProductionHost(hostname)) return;
  /*
   * Il consenso, prima di tutto il resto.
   *
   * Lo script legge informazioni dal terminale — referrer, indirizzo, lingua,
   * risoluzione — e l'art. 5(3) ePrivacy copre l'ACCESSO a quelle informazioni
   * a prescindere dal fatto che siano dati personali. La misura d'audience non
   * rientra nell'esenzione «strettamente necessario», e il §25 TDDDG tedesco —
   * la BfDI e' l'autorita' dichiarata nel nostro ROPA — non prevede alcuna
   * esenzione per gli analytics.
   *
   * Finora la riga in `main.tsx` lo caricava prima e a prescindere dal banner:
   * anche chi aveva RIFIUTATO veniva misurato. Chi accetta non deve invece
   * aspettare un ricaricamento — il banner richiama questa funzione appena
   * l'accettazione e' registrata.
   */
  if (!isAnalyticsAllowed()) return;
  /* Chi riaccende dopo aver spento: senza questa riga lo script ripartirebbe
     con addosso l'interruttore di `fermaUmami`, cioe' a vuoto. */
  try { localStorage.removeItem('umami.disabled'); } catch { /* vedi fermaUmami */ }
  // Idempotent: never inject the script twice.
  if (document.querySelector('script[data-website-id]')) {
    quandoPronto?.();
    return;
  }
  const s = document.createElement('script');
  s.defer = true;
  s.src = UMAMI_SRC;
  s.setAttribute('data-website-id', UMAMI_WEBSITE_ID);
  if (quandoPronto) s.addEventListener('load', () => { quandoPronto(); });
  /*
   * La stringa di ricerca fuori dall'indirizzo registrato.
   *
   * I collegamenti profondi arrivano con `?tournament=…&t=…`, `?challenge=…`,
   * `?puzzle=…`: ogni gettone diverso faceva un PERCORSO diverso, cosi' la
   * stessa schermata si spezzava in decine di righe da una visita l'una, e il
   * gettone d'invito — che e' un segreto — finiva scritto nel registro delle
   * visite. Con questo resta il percorso, senza la coda.
   */
  s.setAttribute('data-exclude-search', 'true');
  document.head.appendChild(s);
}

/**
 * Spegne la misura per questo browser, subito e per le visite successive.
 *
 * Serve a chi cambia idea: il rifiuto dal banner e l'interruttore nelle
 * impostazioni del profilo. Togliere il tag dello script NON basta — una volta
 * caricato, Umami ha gia' agganciato i suoi ascoltatori e continuerebbe a
 * mandare le visite per tutto il resto della sessione.
 *
 * `umami.disabled` in `localStorage` e' l'interruttore che lo script stesso
 * guarda a ogni evento: e' il solo modo di fermarlo davvero senza ricaricare
 * la pagina. Si toglie il tag lo stesso, cosi' un eventuale
 * ricaricamento parte pulito, e si azzera `window.umami`, cosi' anche le
 * nostre chiamate diventano subito inerti.
 */
export function fermaUmami(): void {
  try {
    localStorage.setItem('umami.disabled', '1');
  } catch {
    /* Navigazione privata, spazio esaurito: peggio di cosi' non si puo' fare,
       e fallire qui non deve rompere il gesto dell'utente. */
  }
  document.querySelectorAll('script[data-website-id]').forEach((s) => { s.remove(); });
  delete (window as { umami?: unknown }).umami;
}
